/**
 * Which trace sample rate a given route gets, and under which name it is
 * grouped.
 *
 * Pure policy, deliberately separate from `sentry.client.config.ts`: importing
 * that file runs `Sentry.init`, so nothing there can be exercised at a rate
 * other than the one this build happens to carry. Everything here is a plain
 * function over a pathname.
 */

/**
 * Base trace sample rate when `VITE_SENTRY_TRACES_SAMPLE_RATE` is unset or
 * unusable.
 *
 * 1.0 rather than the old 0.1 because the flat rate was spending the org's whole
 * span quota on traffic that answers nothing. Measured 2026-09-09 over 7d on
 * `frontend-prd`: `/Embed/*` + `/embed/*` are 6,750,360 of 6,849,080 frontend
 * spans — 98.56% — so the entire Nexus app is 1.44% of what we pay for, and only
 * two of 25 frontend routes clear n=300 samples/week. Both are the widget. Six
 * app routes have exactly ONE sampled event in a week, and their reported p95 is
 * that single event's duration.
 *
 * Sampling the widget at a hundredth of the app is therefore not a cut, it is a
 * reallocation: `/app/inbox` goes from 93 samples/week to 930 — the first
 * Nexus-app route to clear n=300 — while total demand falls, because 98.56% of
 * the volume is what gets cut.
 */
export const DEFAULT_TRACES_SAMPLE_RATE = 1.0

/**
 * The widget's share of the base rate.
 *
 * A FRACTION rather than its own env var on purpose. The lever that buys
 * statistical power is the RATIO between widget and app traffic, not either
 * absolute rate — so an operator turning `VITE_SENTRY_TRACES_SAMPLE_RATE` down
 * for quota reasons takes the saving proportionally out of the 98.56%, and keeps
 * the ratio. A second variable would also have to be classified in
 * `src/config/env.classification.ts` and then set, in every environment, by someone who
 * knows what it means — and one nobody sets is one more thing to go stale.
 *
 * Being <= 1 also makes the widget rate structurally incapable of exceeding the
 * base rate, so there is no ordering to get wrong.
 */
export const EMBED_TRACE_SAMPLE_FRACTION = 0.01

/**
 * A sample rate the SDK will accept, or `fallback`.
 *
 * 🔴 NOT defensive decoration. `@sentry/core` 10.27.0 `parseSampleRate` accepts
 * only a boolean or a number in [0, 1]; ANYTHING else makes `sampleSpan` return
 * `[false]` and DROP the root span (`tracing/sampling.js`). It fails closed and
 * says nothing outside a debug build. So `parseFloat("")` → `NaN` on a
 * misconfigured environment does not fall back to a default — it silently turns
 * tracing off entirely. Verified 2026-09-09 against the installed SDK: a sampler
 * returning `NaN`, `undefined`, `null`, `"abc"`, `2` or `-1` yields
 * `spanIsSampled === false` in every case, against controls returning `1`
 * (sampled) and `0` (dropped).
 */
export function usableSampleRate(value: number, fallback: number): number {
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : fallback
}

/** The one spelling of the widget route, in the case the router actually serves. */
const EMBED_PATH_PREFIX = "/embed"

/**
 * True when `pathname` names the embed widget, in EITHER case.
 *
 * The two spellings are one route. `embed-widget-generator.util.ts` renders
 * `"{{&domain}}/Embed/{{embedId}}"`, `common/routing/legacy-redirects.ts` maps
 * `/Embed/:embedId` onto `/embed/:embedId`, and the browser therefore emits a
 * pageload transaction under the capital spelling and a navigation under the
 * lowercase one. Sentry sees two route groups and two percentile populations for
 * one widget — 64.3% and 34.4% of frontend transactions, measured 2026-09-09.
 *
 * 🔴 Case-insensitive HERE and nowhere else. The capital `/Embed/` is
 * load-bearing in the application: `common/routing/RouteProtection.tsx`'s
 * `isEmbedPage` tests `pathname.startsWith("/embed/")` case-SENSITIVELY, so
 * folding case in the router would change who is served what. This module is
 * telemetry and is read by nothing else.
 *
 * The prefix matches only at a segment boundary, so `/embed` and `/embed/x?y`
 * match while `/embedded` and `/test-embed` (a real route, `routes.tsx`) do not.
 */
export function isEmbedPath(pathname: unknown): boolean {
  if (typeof pathname !== "string") return false
  if (pathname.slice(0, EMBED_PATH_PREFIX.length).toLowerCase() !== EMBED_PATH_PREFIX) return false
  const next = pathname.charAt(EMBED_PATH_PREFIX.length)
  return next === "" || next === "/" || next === "?"
}

/**
 * `/Embed/abc123` → `/embed/abc123`, for TELEMETRY GROUPING ONLY.
 *
 * Rewrites the leading segment and nothing else, so the widget id keeps its
 * bytes — ids are case-sensitive and a whole-string `toLowerCase()` would mint a
 * different one. Applied in `beforeSendTransaction`, i.e. after the sampling
 * decision and on the outbound event only; no router, redirect or guard reads
 * this value.
 */
export function normalizeEmbedTransactionName(name: string): string {
  return isEmbedPath(name) ? EMBED_PATH_PREFIX + name.slice(EMBED_PATH_PREFIX.length) : name
}

/**
 * The `tracesSampler`, built over a base rate so it can be exercised at rates
 * other than this build's.
 *
 * v10 shape, which is NOT the backend's. `apps/backend/src/_common/sentry/sentry.config.ts`
 * runs the same per-path idea on `@sentry/node` v7, where the name lives at
 * `samplingContext.transactionContext.name`; in `@sentry/core` 10.27.0 it is
 * `samplingContext.name` (`tracing/trace.js`, `_startRootSpan`), and reading the
 * v7 path here would yield `undefined` for every transaction — which drops every
 * trace rather than erroring.
 *
 * The value is a raw pathname: `browserTracingIntegration` names a pageload
 * `WINDOW.location.pathname` and a navigation the destination's pathname
 * (`tracing/browserTracingIntegration.js`). There is no `"GET "` prefix to strip
 * as there is on the backend.
 *
 * 🔴 It also deliberately omits the backend's `parentSampled` branch. In v10 a
 * `tracesSampler` takes precedence over parent sampling, and the opt-in is
 * `samplingContext.inheritOrSampleWith`. The browser is where these traces
 * START — no server renders a `sentry-trace` meta tag into this SPA — and
 * `tracePropagationTargets` is unset, so the SDK attaches headers to same-origin
 * requests only while the API is a separate origin (`VITE_BACKEND_URL`, e.g.
 * `https://api.gpt.nexus`). Verified 2026-09-09 against the installed
 * `shouldAttachHeaders`: same-origin absolute `true`, relative `true`,
 * `https://api.gpt.nexus/...` `false`. So there is no parent decision to inherit
 * here, and raising the app rate does not raise the backend project's volume.
 *
 * 🔴 MUST NOT THROW. `_startRootSpan` calls this with no try/catch, so an
 * exception propagates out of the SDK's pageload and navigation handlers.
 * Verified 2026-09-09 against the installed SDK: a throwing sampler escapes
 * `startSpan` verbatim, where every invalid RETURN is merely swallowed as a
 * drop. Both branches below are numbers by construction — the base is clamped,
 * and the fraction is a literal in (0, 1].
 */
export function createTracesSampler(
  baseSampleRate: number
): (samplingContext: { name?: unknown }) => number {
  const base = usableSampleRate(baseSampleRate, DEFAULT_TRACES_SAMPLE_RATE)
  const embed = base * EMBED_TRACE_SAMPLE_FRACTION
  return (samplingContext) => (isEmbedPath(samplingContext?.name) ? embed : base)
}
